import { RESOURCE_NAMES } from './resources.js';
/**
 * All science definitions, per `docs/game-details/sciences.md`.
 *
 * `researchCost` is a step-based cost/time curve — see {@link ScienceCostStep}
 * in `science.types.ts`. Every science has a Level 1 step; cost doubles and
 * time scales ×1.5 per level from the closest earlier step. Physics and
 * Mathematics each get a second step where Gas enters the mix (their Level-1
 * cost has `gas: 0`, but the docs give a second sample — Physics L5 = 1250
 * gas, Mathematics L8 = 8000 gas — that doesn't fit a straight doubling of
 * 0). The step's `timeSeconds` at those levels is just the plain-formula
 * continuation from Level 1 (rounded to the nearest second) — there's no
 * documented time anomaly for either, only a cost one.
 *
 * `requirements` reference `laboratory` — the single generic building slot
 * the docs call "Research Center / Experimental Facility / Overmind" (race-flavored
 * names for the same building, per `BUILDING_DEFINITIONS.laboratory`).
 *
 * The lookup/scaling formula itself (`cost(level) = step.cost × 2^(level −
 * step.atLevel)`, `time(level) = step.timeSeconds × 1.5^(level − step.atLevel)`,
 * plus the Research Center reduction) lives in `packages/game-engine/src/sciences.ts`,
 * not here.
 */
export const SCIENCE_DEFINITIONS = {
    // -----------------------------------------------------------------------
    // Fundamental
    // -----------------------------------------------------------------------
    mathematics: {
        id: "mathematics",
        name: "Mathematics",
        description: "Increases the amount of resources gained on Planets",
        category: "fundamental",
        maxLevel: 10,
        researchCost: [
            { atLevel: 1, cost: { metal: 1000, minerals: 400, gas: 0 }, timeSeconds: 897 }, // 00:14:57
            { atLevel: 8, cost: { metal: 128000, minerals: 51200, gas: 8000 }, timeSeconds: 15326 },
        ],
        requirements: [{ type: "building", id: "laboratory", level: 1 }],
    },
    physics: {
        id: "physics",
        name: "Physics",
        description: "Increases Energy production",
        category: "fundamental",
        maxLevel: 10,
        researchCost: [
            { atLevel: 1, cost: { metal: 1000, minerals: 500, gas: 0 }, timeSeconds: 1443 }, // 00:24:03
            { atLevel: 5, cost: { metal: 16000, minerals: 8000, gas: 1250 }, timeSeconds: 7305 },
        ],
        requirements: [{ type: "building", id: "laboratory", level: 1 }],
    },
    chemistry: {
        id: "chemistry",
        name: "Chemistry",
        description: "Reduces fuel consumption of ships",
        category: "fundamental",
        maxLevel: 15,
        researchCost: [
            { atLevel: 1, cost: { metal: 400, minerals: 200, gas: 50 }, timeSeconds: 722 }, // 00:12:02
        ],
        requirements: [{ type: "building", id: "laboratory", level: 3 }],
    },
    astronomy: {
        id: "astronomy",
        name: "Astronomy",
        description: "Increases the speed of ships",
        category: "fundamental",
        maxLevel: 15,
        researchCost: [
            { atLevel: 1, cost: { metal: 500, minerals: 0, gas: 500 }, timeSeconds: 1505 }, // 00:25:05
        ],
        requirements: [{ type: "building", id: "laboratory", level: 2 }],
    },
    ecology: {
        id: "ecology",
        name: "Ecology",
        description: `Increases the amount of ${RESOURCE_NAMES.ozone} recovered on Planets`,
        category: "fundamental",
        maxLevel: 15,
        researchCost: [
            { atLevel: 1, cost: { metal: 1000, minerals: 750, gas: 250 }, timeSeconds: 1505 }, // 00:25:05
            { atLevel: 11, cost: { metal: 640000, minerals: 480000, gas: 160000 }, timeSeconds: 90000 }, // 00:25:05
            { atLevel: 12, cost: { metal: 800000, minerals: 600000, gas: 200000 }, timeSeconds: 135000 }, // 00:25:05
            { atLevel: 13, cost: { metal: 1000000, minerals: 750000, gas: 250000 }, timeSeconds: 200000 }, // 00:25:05
            { atLevel: 14, cost: { metal: 1250000, minerals: 850000, gas: 350000 }, timeSeconds: 300000 }, // 00:25:05
            { atLevel: 15, cost: { metal: 1500000, minerals: 1000000, gas: 400000 }, timeSeconds: 450000 }, // 00:25:05
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 4 },
            { type: "science", id: "physics", level: 3 },
            { type: "science", id: "chemistry", level: 3 },
        ],
    },
    // -----------------------------------------------------------------------
    // Engineering
    // -----------------------------------------------------------------------
    architecture: {
        id: "architecture",
        name: "Architecture",
        description: "Reduces building construction costs",
        category: "engineering",
        maxLevel: 10,
        researchCost: [
            { atLevel: 1, cost: { metal: 10000, minerals: 5000, gas: 0 }, timeSeconds: 10082 }, // 02:48:02
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 4 },
            { type: "science", id: "mathematics", level: 5 },
            { type: "science", id: "physics", level: 5 },
        ],
    },
    signalSystems: {
        id: "signalSystems",
        name: "Signal Systems",
        description: "Increases the maximum number of simultaneous flights allowed",
        category: "engineering",
        maxLevel: 15,
        researchCost: [
            { atLevel: 1, cost: { metal: 0, minerals: 250, gas: 500 }, timeSeconds: 845 }, // 00:14:05
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 4 },
            { type: "science", id: "astronomy", level: 1 },
        ],
    },
    rockets: {
        id: "rockets",
        name: "Rocket Fuel",
        description: "Increases the speed of ships (15% per level)",
        category: "engineering",
        maxLevel: 15,
        researchCost: [
            { atLevel: 1, cost: { metal: 500, minerals: 1000, gas: 200 }, timeSeconds: 722 }, // 00:12:02
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 6 },
            { type: "science", id: "chemistry", level: 4 },
        ],
    },
    aerodynamics: {
        id: "aerodynamics",
        name: "Aerodynamics",
        description: "Increases the speed of ships (15% per level)",
        category: "engineering",
        maxLevel: 15,
        researchCost: [
            { atLevel: 1, cost: { metal: 0, minerals: 1000, gas: 500 }, timeSeconds: 2876 }, // 00:47:56
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 7 },
            { type: "science", id: "chemistry", level: 6 },
            { type: "science", id: "defenceSystems", level: 5 },
        ],
    },
    wormholes: {
        id: "wormholes",
        name: "Wormholes",
        description: "Increases the speed of ships (20% per level)",
        category: "engineering",
        maxLevel: 15,
        researchCost: [
            { atLevel: 1, cost: { metal: 2500, minerals: 3750, gas: 1500 }, timeSeconds: 3485 }, // 00:58:05
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 10 },
            { type: "science", id: "aerodynamics", level: 3 },
        ],
    },
    gravitationalManipulators: {
        id: "gravitationalManipulators",
        name: "Gravitational Manipulators",
        description: "Allows the construction of each race's most powerful ship",
        category: "engineering",
        maxLevel: 1,
        researchCost: [
            { atLevel: 1, cost: {}, timeSeconds: 4680000 }, // 1300:00:00
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 15 },
            { type: "science", id: "physics", level: 10 },
            { type: "science", id: "mathematics", level: 10 },
            { type: "science", id: "astronomy", level: 15 },
        ],
        requiredFreeEnergy: 250000,
    },
    // -----------------------------------------------------------------------
    // Warfare
    // -----------------------------------------------------------------------
    intelligence: {
        id: "intelligence",
        name: "Intelligence",
        description: "Increases the chance of successful spying and allows for more spy checks",
        category: "warfare",
        maxLevel: 20,
        researchCost: [
            { atLevel: 1, cost: { metal: 50, minerals: 100, gas: 50 }, timeSeconds: 2165 }, // 00:36:05
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 4 },
            { type: "science", id: "astronomy", level: 2 },
        ],
    },
    defenceSystems: {
        id: "defenceSystems",
        name: "Defence Systems",
        description: "Increases the life of ships",
        category: "warfare",
        maxLevel: 20,
        researchCost: [
            { atLevel: 1, cost: { metal: 100, minerals: 50, gas: 0 }, timeSeconds: 1557 }, // 00:25:57
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 5 },
            { type: "science", id: "physics", level: 4 },
        ],
    },
    laser: {
        id: "laser",
        name: "Laser",
        description: "Increases the attack of ships with Laser weapons",
        category: "warfare",
        maxLevel: 15,
        researchCost: [
            { atLevel: 1, cost: { metal: 200, minerals: 100, gas: 0 }, timeSeconds: 598 }, // 00:09:58
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 8 },
            { type: "science", id: "mathematics", level: 3 },
        ],
    },
    ion: {
        id: "ion",
        name: "Ion",
        description: "Increases the attack of ships with Ion weapons",
        category: "warfare",
        maxLevel: 15,
        researchCost: [
            { atLevel: 1, cost: { metal: 500, minerals: 250, gas: 50 }, timeSeconds: 1196 }, // 00:19:56
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 8 },
            { type: "science", id: "mathematics", level: 5 },
            { type: "science", id: "laser", level: 5 },
        ],
    },
    plasma: {
        id: "plasma",
        name: "Plasma",
        description: "Increases the attack of ships with Plasma weapons",
        category: "warfare",
        maxLevel: 15,
        researchCost: [
            { atLevel: 1, cost: { metal: 1000, minerals: 1000, gas: 1000 }, timeSeconds: 1804 }, // 00:30:04
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 8 },
            { type: "science", id: "mathematics", level: 7 },
            { type: "science", id: "laser", level: 10 },
            { type: "science", id: "ion", level: 5 },
        ],
    },
    polymerShields: {
        id: "polymerShields",
        name: "Polymer Shields",
        description: "Increases the Light armor of ships and defence",
        category: "warfare",
        maxLevel: 10,
        researchCost: [
            { atLevel: 1, cost: { metal: 1000, minerals: 500, gas: 250 }, timeSeconds: 1258 }, // 00:20:58
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 8 },
            { type: "science", id: "laser", level: 2 },
        ],
    },
    carbonFiberShields: {
        id: "carbonFiberShields",
        name: "Carbon Fiber Shields",
        description: "Increases the Medium armor of ships and defence",
        category: "warfare",
        maxLevel: 10,
        researchCost: [
            { atLevel: 1, cost: { metal: 1300, minerals: 650, gas: 325 }, timeSeconds: 1258 }, // 00:20:58
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 9 },
            { type: "science", id: "ion", level: 2 },
        ],
    },
    heavyMetalShields: {
        id: "heavyMetalShields",
        name: "Heavy Metal Shields",
        description: "Increases the Heavy armor of ships and defence",
        category: "warfare",
        maxLevel: 10,
        researchCost: [
            { atLevel: 1, cost: { metal: 1600, minerals: 800, gas: 400 }, timeSeconds: 1258 }, // 00:20:58
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 10 },
            { type: "science", id: "plasma", level: 2 },
        ],
    },
    // -----------------------------------------------------------------------
    // Warfare tactics (only one may be researched per player — see
    // `EXCLUSIVE_SCIENCE_IDS` below and `canResearchAdditionalScience` in
    // game-engine)
    // -----------------------------------------------------------------------
    offensiveTactics: {
        id: "offensiveTactics",
        name: "Offensive Tactics",
        description: "Increases the attack of ships, regardless of weapon type",
        category: "warfare",
        maxLevel: 10,
        researchCost: [
            { atLevel: 1, cost: { metal: 50000, minerals: 25000, gas: 5000 }, timeSeconds: 10649 }, // 02:57:29
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 20 },
            { type: "science", id: "laser", level: 10 },
            { type: "science", id: "ion", level: 7 },
            { type: "science", id: "plasma", level: 5 },
        ],
    },
    defensiveTactics: {
        id: "defensiveTactics",
        name: "Defensive Tactics",
        description: "Increases the life of ships",
        category: "warfare",
        maxLevel: 10,
        researchCost: [
            { atLevel: 1, cost: { metal: 0, minerals: 50000, gas: 5000 }, timeSeconds: 10649 }, // 02:57:29
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 20 },
            { type: "science", id: "defenceSystems", level: 10 },
            { type: "science", id: "heavyMetalShields", level: 5 },
        ],
    },
    surpriseTactics: {
        id: "surpriseTactics",
        name: "Surprise Tactics",
        description: "Gives a chance for ships to inflict 2 to 2.5 times the damage",
        category: "warfare",
        maxLevel: 10,
        researchCost: [
            { atLevel: 1, cost: { metal: 50000, minerals: 30000, gas: 0 }, timeSeconds: 10649 }, // 02:57:29
        ],
        requirements: [
            { type: "building", id: "laboratory", level: 20 },
            { type: "science", id: "laser", level: 7 },
            { type: "science", id: "ion", level: 7 },
            { type: "science", id: "plasma", level: 7 },
        ],
    },
};
/** Derived from `SCIENCE_DEFINITIONS` so the two never drift apart. */
export const SCIENCE_MAX_LEVELS = Object.fromEntries(Object.entries(SCIENCE_DEFINITIONS).map(([id, def]) => [id, def.maxLevel]));
/**
 * Sciences where only one of the group may ever be researched per player
 * (see `canResearchAdditionalScience` in `packages/game-engine/src/sciences.ts`).
 * Previously these were the sole members of a dedicated "additional"
 * category; now they're a distinguished, mutually-exclusive subset of
 * Warfare.
 */
export const EXCLUSIVE_SCIENCE_IDS = [
    "offensiveTactics", "defensiveTactics", "surpriseTactics",
];
/**
 * Research that can never be instantly completed with Dark Matter
 * (`/rushScienceResearch` — see `isScienceRushable` in
 * `packages/game-engine/src/sciences.ts`). `gravitationalManipulators` gates
 * each race's most powerful ship behind its full 1300h research time by
 * design — rushing it would undercut that gate entirely.
 */
export const NON_RUSHABLE_SCIENCE_IDS = ["gravitationalManipulators"];
export const DEFAULT_SCIENCES = {
    mathematics: 0,
    physics: 0,
    chemistry: 0,
    astronomy: 0,
    ecology: 0,
    architecture: 0,
    signalSystems: 0,
    rockets: 0,
    aerodynamics: 0,
    wormholes: 0,
    gravitationalManipulators: 0,
    intelligence: 0,
    defenceSystems: 0,
    laser: 0,
    ion: 0,
    plasma: 0,
    polymerShields: 0,
    carbonFiberShields: 0,
    heavyMetalShields: 0,
    offensiveTactics: 0,
    defensiveTactics: 0,
    surpriseTactics: 0,
};
/**
 * Old `ScienceId` -> new `ScienceId`, for GitHub issue #76's rename (see
 * `science-rename-issue-76` project notes). Existing players' `sciences` map
 * on `universes/{u}/players/{uid}` still has levels stored under these old
 * keys — `resolveSciences` (apps/functions/src/advance.ts) and the mock
 * profile service read the new names only, so until migrated, researched
 * levels under an old key are silently treated as 0.
 *
 * Consumed by `apps/functions/src/scripts/migrate-science-ids.ts` (one-off
 * Firestore migration) and the mock profile service (so local dev sessions
 * started before the rename don't lose progress). Ids that didn't change
 * (mathematics, physics, chemistry, astronomy, ecology, laser, ion, plasma)
 * are intentionally absent.
 */
export const LEGACY_SCIENCE_ID_RENAMES = {
    ultimateBuilding: "architecture",
    radarSystems: "signalSystems",
    fuelCells: "rockets",
    jetEngines: "aerodynamics",
    hyperspace: "wormholes",
    parallelUniverses: "gravitationalManipulators",
    espionage: "intelligence",
    shipDefense: "defenceSystems",
    laserScience: "laser",
    ionScience: "ion",
    plasmaScience: "plasma",
    lightArmor: "polymerShields",
    mediumArmor: "carbonFiberShields",
    heavyArmor: "heavyMetalShields",
    forceAttack: "offensiveTactics",
    promptDefense: "defensiveTactics",
    criticalDamage: "surpriseTactics",
};
//# sourceMappingURL=sciences.js.map